import { Select } from "antd"
import dayjs from "dayjs"
import { t } from "i18next"
import { useEffect, useMemo, useState } from "react"
import DateTime from "utils/dateTime"

const generateStartTimeOptions = (minuteStep = 15, earliestTime = null, startDate = null, enableAllTimesToday) => {
  const options = []
  const isToday = startDate && DateTime.isSameDay(startDate, dayjs())

  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += minuteStep) {
      const time = dayjs().hour(hour).minute(min).second(0)
      if (isToday && !enableAllTimesToday && earliestTime && time.isBefore(earliestTime)) continue
      const value = time.format("HH:mm")
      options.push({
        label: time.format("h:mm a"),
        value,
      })
    }
  }
  return options
}

const getNearestFutureTime = (minuteStep = 15) => {
  const now = dayjs()
  const nearestMinutes = Math.ceil(now.minute() / minuteStep) * minuteStep
  return now.minute(nearestMinutes).second(0).format("HH:mm")
}

const addMinutes = (time, minutes) => {
  return dayjs(time, "HH:mm").add(minutes, "minute").format("HH:mm")
}

const getDurationLabel = (start, end) => {
  const diff = end.diff(start, "minute")

  if (diff < 0) return ""

  const dur = dayjs.duration(diff, "minute")
  const days = Math.floor(dur.asDays())
  const hours = dur.hours()
  const mins = dur.minutes()

  const parts = []
  if (days) parts.push(`${days}d`)
  if (hours) parts.push(`${hours}h`)
  if (mins) parts.push(`${mins}m`)

  return `(${parts.join(" ") || "0 mins"})`
}

const generateEndTimeOptions = (minuteStep = 15, startTime, endDate = null, startDate = null) => {
  const options = []
  const baseDate = startDate ? dayjs(startDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
  const earliestEndTime = dayjs(`${baseDate} ${startTime}`, "YYYY-MM-DD HH:mm")

  const endLimit = endDate
    ? dayjs(endDate).hour(23).minute(45).second(0)
    : dayjs(startDate).hour(23).minute(45).second(0)

  let current =
    (endDate && DateTime.isSameDay(baseDate, endDate)) || !endDate
      ? earliestEndTime
      : DateTime.isSameDay(endDate, dayjs())
      ? dayjs(`${dayjs().format("YYYY-MM-DD")} ${startTime}`, "YYYY-MM-DD HH:mm")
      : (endDate?.isBefore(dayjs()) || DateTime?.isAfterCurrentDay(endDate)) && dayjs(endDate)?.startOf("day")

  while (current?.isSameOrBefore(endLimit)) {
    const value = current?.format("HH:mm")
    const label = `${current?.format("h:mm a")} ${getDurationLabel(earliestEndTime, current)}`
    options.push({ value, label })
    current = current?.add(minuteStep, "minute")
  }
  return options
}

const TimeRangeSelector = ({
  value = [],
  onChange,
  isRangePicker = true,
  disabled,
  endDate = null,
  startDate = null,
  allowCustomTimes = true,
  enableAllTimesToday = false,
}) => {
  const minSelectableStart = useMemo(() => {
    const rounded = getNearestFutureTime()
    return dayjs(rounded, "HH:mm")
  }, [])

  const [startTime, setStartTime] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const [startInput, setStartInput] = useState("")
  const [endInput, setEndInput] = useState("")

  const startOptions = useMemo(
    () => generateStartTimeOptions(15, minSelectableStart, startDate, enableAllTimesToday),
    [minSelectableStart, startDate]
  )
  const endOptions = useMemo(
    () => (startTime ? generateEndTimeOptions(15, startTime, endDate, startDate) : []),
    [startTime, endDate, startDate]
  )

  useEffect(() => {
    if (!value?.[0] || !value?.[1]) {
      const start = minSelectableStart.format("HH:mm")
      const end = addMinutes(start, 60)
      setStartTime(start)
      setEndTime(end)
      onChange?.([dayjs(start, "HH:mm"), dayjs(end, "HH:mm")])
    }
  }, [])

  useEffect(() => {
    if (value?.[0]?.isValid?.() && value?.[1]?.isValid?.()) {
      setStartTime(value[0].format("HH:mm"))
      setEndTime(value[1].format("HH:mm"))
    }
  }, [value])

  const handleOnClear = () => {
    setStartTime(null)
    setEndTime(null)
    onChange?.(null)
  }

  const handleStartChange = val => {
    if (!val) return handleOnClear()

    const now = dayjs()
    const baseDate = dayjs(startDate).format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD")
    const selectedStart = dayjs(`${baseDate} ${val}`, "YYYY-MM-DD HH:mm")
    const isToday = startDate && DateTime.isSameDay(startDate, now)

    if (isToday && !enableAllTimesToday && selectedStart.isBefore(now)) return

    const lastPossibleEnd = dayjs(endDate || startDate || undefined)
      .hour(23)
      .minute(59)
    const newEnd = selectedStart.add(60, "minute")
    const end = newEnd.isAfter(lastPossibleEnd) ? lastPossibleEnd : newEnd

    setStartTime(val)
    setEndTime(end.format("HH:mm"))
    onChange?.([selectedStart, end])
  }

  const handleEndChange = val => {
    if (!val) return handleOnClear()

    const baseStartDate = startDate ? dayjs(startDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
    const baseEndDate = endDate ? dayjs(endDate).format("YYYY-MM-DD") : dayjs(baseStartDate).format("YYYY-MM-DD")

    const selectedStart = dayjs(`${baseStartDate} ${startTime}`, "YYYY-MM-DD HH:mm")
    const selectedEnd = dayjs(`${baseEndDate} ${val}`, "YYYY-MM-DD HH:mm")
    const isSameDay = (startDate && endDate && DateTime.isSameDay(startDate, endDate)) || !endDate

    if (isSameDay && selectedEnd.isBefore(selectedStart)) return

    const lastPossibleEnd = dayjs(endDate || startDate || undefined)
      .hour(23)
      .minute(59)

    if (selectedEnd.isAfter(lastPossibleEnd)) return

    setEndTime(val)
    onChange?.([selectedStart, selectedEnd])
  }

  const isValidTime = str => dayjs(str, ["H:mm", "HH:mm", "H:m", "HH:m"], false).isValid()

  const getAmPmOptions = input => {
    const parsed = dayjs(input, ["H:mm", "HH:mm", "H:m", "HH:m"], false)
    if (!parsed.isValid()) return []

    const hourInput = parsed.hour()
    const minuteInput = parsed.minute()

    const am = parsed.hour(hourInput).minute(minuteInput).format("HH:mm")
    const pm = parsed
      .hour(hourInput + 12)
      .minute(minuteInput)
      .format("HH:mm")

    return [
      { value: am, label: dayjs(am, "HH:mm").format("h:mm a") },
      { value: pm, label: dayjs(pm, "HH:mm").format("h:mm a") },
    ]
  } 

  const appendedStartOptions = useMemo(() => {
    const base = [...startOptions]

    if (startTime && isValidTime(startTime) && !startOptions.some(opt => opt.value === startTime)) {
      base.push({ value: startTime, label: dayjs(startTime, "HH:mm").format("h:mm a") })
    }

    if (startInput && isValidTime(startInput)) {
      const ampmOptions = getAmPmOptions(startInput)
      ampmOptions.forEach(opt => {
        if (!base.some(o => o.value === opt.value)) {
          base.push(opt)
        }
      })
    }

    return base
  }, [startInput, startOptions, startTime])

  const appendedEndOptions = useMemo(() => {
    const base = [...endOptions]

    if (endTime && isValidTime(endTime) && !endOptions.some(opt => opt.value === endTime)) {
      base.push({ value: endTime, label: dayjs(endTime, "HH:mm").format("h:mm a") })
    }

    if (endInput && isValidTime(endInput)) {
      const ampmOptions = getAmPmOptions(endInput)
      ampmOptions.forEach(opt => {
        if (!base.some(o => o.value === opt.value)) {
          base.push(opt)
        }
      })
    }

    return base
  }, [endInput, endOptions, endTime])

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
      <Select
        showSearch
        allowClear
        placeholder={t("Start time")}
        options={allowCustomTimes ? appendedStartOptions : startOptions}
        value={startTime}
        onChange={handleStartChange}
        filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
        disabled={disabled}
        onSearch={setStartInput}
      />
      {isRangePicker && (
        <>
          <span style={{ marginInline: 2 }}>{"-"}</span>
          <Select
            showSearch
            allowClear
            placeholder={t("End time")}
            options={allowCustomTimes ? appendedEndOptions : endOptions}
            value={endTime}
            onChange={handleEndChange}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            disabled={disabled}
            onSearch={setEndInput}
          />
        </>
      )}
    </div>
  )
}

export default TimeRangeSelector
